import * as React from "react";

import {ISiteSearchResult, SiteSearchService } from "../Services/SiteSearchService";

interface ISearchResultsPageProps {
    mainTitle: string;
    searchQuery: string;
    resultAmountText: string;
    resultAmountLoadingText: string;
    previousPageText: string;
    nextPageText: string;
    pageSize: number;
    visiblePages: number;
    resultStatsText: string;
    language: string;
}

interface ISearchResultsPageState {
    matches: ISiteSearchResult[];
    resultAmountCount: number;
    currentPage: number;
    isLoading: boolean;
}

class SearchResultsPage extends React.Component<ISearchResultsPageProps, ISearchResultsPageState> {
    private searchService: SiteSearchService;

    public constructor(props: ISearchResultsPageProps) {
        super(props);
        this.state = { matches: [], currentPage: 0, resultAmountCount: 0, isLoading: false };
        this.searchService = new SiteSearchService(props.language);
    }

    public render() {
        const resultsView = this.state.isLoading ? <Spinner/> : this.renderResults();
        return (
            <div>
                <h1 id={"site-search-top"}>{this.props.mainTitle}</h1>
                {this.renderSearchDescription()}
                {this.renderPagination()}

                {resultsView}

                {this.renderVisibleStats()}

                {this.renderPagination()}
            </div>
        );
    }

    public componentDidMount() {
        this.handlePageChange(this.state.currentPage);
    }

    private handlePageChange = (newPageNumber: number) => {
        const topEl = document.getElementById("site-search-top");
        if (topEl) {
            topEl.scrollIntoView();
        }
        this.setState({...this.state, matches: [], isLoading: true });
        this.searchService.getSearchResults(this.props.searchQuery, newPageNumber, this.props.pageSize)
            .then((result) => this.setState({
                currentPage: newPageNumber,
                isLoading: false,
                matches: result.results,
                resultAmountCount: result.totalCount,
            }));
    }

    private renderSearchDescription() {
        if (!this.state.isLoading) {
            return (
                <SearchDescription
                    searchQuery={this.props.searchQuery}
                    resultAmountText={this.props.resultAmountText}
                    resultAmountCount={this.state.resultAmountCount}
                />
            );
        }
        return (
            <SearchDescription
                searchQuery={this.props.searchQuery}
                resultAmountText={this.props.resultAmountLoadingText}
                resultAmountCount={this.state.resultAmountCount}
            />
        );
    }

    private renderResults() {
        return this.state.matches.map(
            (result: ISiteSearchResult) =>
            (
                <SearchResult
                    key={result.url}
                    isFile={result.isFile}
                    mimeType={result.mimeType}
                    size={result.size}
                    text={result.text}
                    url={result.url}
                />
            ),
        );
    }

    private renderPagination() {
        return (
              <Pagination
                    prevPageText={this.props.previousPageText}
                    nextPageText={this.props.nextPageText}
                    resultAmount={this.state.resultAmountCount}
                    pageSize={this.props.pageSize}
                    visiblePages={this.props.visiblePages}
                    currentPage={this.state.currentPage}
                    handlePageChange={this.handlePageChange}
              />
        );
    }

    private renderVisibleStats() {
        if (!this.state.isLoading) {
            return (
                <VisibleStats
                    currentPage={this.state.currentPage}
                    pageSize={this.props.pageSize}
                    resultAmount={this.state.resultAmountCount}
                    resultStatsText={this.props.resultStatsText}
                />
            );
        }
    }

}

interface ISearchDescriptionProps {
    searchQuery: string;
    resultAmountText: string;
    resultAmountCount: number;
}

const SearchDescription: React.FC<ISearchDescriptionProps>
    = ({searchQuery, resultAmountCount, resultAmountText}) => {
    const content = resultAmountText
        .replace("{count}", resultAmountCount.toString());
    const tmp = content.split("{query}");
    return <div>{tmp[0]}<strong>{searchQuery}</strong>{tmp[1]}</div>;
};

interface IPaginationProps {
    currentPage: number;
    visiblePages: number;
    pageSize: number;
    resultAmount: number;
    nextPageText: string;
    prevPageText: string;
    handlePageChange: any;
}

const Pagination: React.FC<IPaginationProps>
    = ({currentPage, visiblePages, pageSize, resultAmount, nextPageText, prevPageText, handlePageChange}) => {
    const lastPageNumber = Math.ceil(resultAmount / pageSize) - 1; // -1 because numbers start from zero
    const previousPageLink =
        currentPage === 0
            ? ""
            : <a className="previous" onClick={() => handlePageChange(currentPage - 1)}>{prevPageText}</a>;
    const nextPageLink =
        currentPage < lastPageNumber
            ? <a className="next" onClick={() => handlePageChange(currentPage + 1)}>{nextPageText}</a>
            : "";

    const pageNumberLinks = () => {
        const startPage = Math.max(currentPage - 2, 0);
        const lastPage = Math.min(lastPageNumber, startPage + visiblePages);
        const pageNumbers = [];
        for (let i = startPage; i <= lastPage; i++) {
            const x = i;
            pageNumbers.push(x);
        }

        return  pageNumbers.map((pageNumber) => {
            const currentPageClass = pageNumber === currentPage ? "current" : "";
            return (
                <a
                    key={pageNumber}
                    className={"page " + currentPageClass}
                    onClick={() => handlePageChange(pageNumber)}
                >{pageNumber + 1}
                </a>);
        });
    };

    return <div className="search-pagination">{previousPageLink} {pageNumberLinks()} {nextPageLink}</div>;
};

interface IVisibleStatsProps {
    pageSize: number;
    currentPage: number;
    resultAmount: number;
    resultStatsText: string;
}
const VisibleStats: React.FC<IVisibleStatsProps>
    = ({pageSize, currentPage, resultAmount, resultStatsText}) => {
    const firstResult = (currentPage * pageSize) + 1;
    const lastResult = Math.min(((currentPage + 1) * pageSize), resultAmount);
    const stats = ` ${firstResult} - ${lastResult} / ${resultAmount} `;

    return <div>{resultStatsText.replace("{stats}", stats)}</div>;
};

interface ISearchResultProps extends ISiteSearchResult {
}

const SearchResult: React.FC<ISearchResultProps> = (props) => {
    const linkTarget = props.isFile ? "_blank" : "";

    return (
        <a href={props.url} target={linkTarget} className={"search-result"}>
            <strong className="linkHeader">{props.text}</strong>
            { props.isFile && <FileInfo isFile={props.isFile} mimeType={props.mimeType} size={props.size} /> }
            <div className="result-ulr">{props.url}</div>
        </a>
    );
};

interface IFileInfoProps {
    isFile: boolean;
    size: number;
    mimeType: string;
}

const FileInfo: React.FC<IFileInfoProps> = ({isFile, size, mimeType}) => {
    const iconPath = mimeType === "application/pdf"
        ? "/content/img/icons/pdf.png"
        : "/content/img/icons/blank.png";

    const iconAlt = mimeType === "application/pdf"
        ? "pdf icon"
        : "file icon";

    const icon = isFile ? <img alt={iconAlt} className={"icon"} src={iconPath} /> : "";
    return isFile
        ? (<span className={"file-info"}>{icon} {size} mb</span>)
        : <span />;
};

export { SearchResultsPage };

const Spinner: React.FC<any> = (props) => {
    return ( <div aria-busy="true" aria-label="Loading" role="progressbar" className="search-spinner" />);
};
